import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useGetInvolvedModal } from "@/contexts/GetInvolvedModalContext";
import { useLanguage } from "@/contexts/LanguageContext";
import Marquee from "react-fast-marquee";
import { Leaf, Drop, TreeEvergreen, Waves, ShieldCheck, Users, X, ArrowSquareOut, ArrowSquareIn, CaretLeft, CaretRight } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FALLBACK_BANNERS = [
  { image_url: "https://customer-assets.emergentagent.com/job_brief-builder-33/artifacts/gsi6lm0t_Bellandur%20lake%20before%20desilting.png", chip_title: "Before", external: true },
  { image_url: "https://customer-assets.emergentagent.com/job_summ-ai-1/artifacts/ud74qsxt_Bellandur%20lake%20with%20full%20water.jpg", chip_title: "After", external: true },
];

const focusIcons = [Leaf, Drop, TreeEvergreen, Waves, ShieldCheck, Users];

export default function HomePage() {
  const { openModal } = useGetInvolvedModal();
  const { t } = useLanguage();
  const marqueeItems = t("marquee_items") || [];
  const focusAreas = t("focus_areas") || [];
  const [banners, setBanners] = useState(FALLBACK_BANNERS);
  const [heroIdx, setHeroIdx] = useState(0);
  const [timelinePictures, setTimelinePictures] = useState([]);
  const [timelineIdx, setTimelineIdx] = useState(0);
  const [popupList, setPopupList] = useState([]);
  const [popupIdx, setPopupIdx] = useState(0);
  const [popupVisible, setPopupVisible] = useState(false);
  const activePopup = popupList.length > 0 ? popupList[popupIdx] : null;

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/banners`)
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          setBanners(data.map((b) => ({ ...b, external: false })));
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/timeline-pictures`)
      .then((r) => r.json())
      .then((data) => setTimelinePictures(Array.isArray(data) ? data : []))
      .catch(() => setTimelinePictures([]));
  }, []);

  // Fetch and show all popups on every load
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/popups`)
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          setPopupList(data);
          setPopupIdx(0);
          setTimeout(() => setPopupVisible(true), 800);
        }
      })
      .catch(() => {});
  }, []);

  const dismissPopup = () => {
    setPopupVisible(false);
    setTimeout(() => { setPopupList([]); setPopupIdx(0); }, 300);
  };

  useEffect(() => {
    if (banners.length <= 1) return;
    const id = setInterval(() => {
      setHeroIdx((i) => (i + 1) % banners.length);
    }, 3000);
    return () => clearInterval(id);
  }, [banners.length]);

  // Reset index if banners array shrinks
  useEffect(() => {
    if (heroIdx >= banners.length) setHeroIdx(0);
  }, [banners.length, heroIdx]);

  useEffect(() => {
    if (timelinePictures.length <= 1) return;
    const id = setInterval(() => {
      setTimelineIdx((i) => (i + 1) % timelinePictures.length);
    }, 2000);
    return () => clearInterval(id);
  }, [timelinePictures.length]);

  useEffect(() => {
    if (timelineIdx >= timelinePictures.length) setTimelineIdx(0);
  }, [timelinePictures.length, timelineIdx]);

  const currentChip = banners[heroIdx]?.chip_title;
  const currentTimelinePicture = timelinePictures[timelineIdx];
  const previousTimelinePicture = timelinePictures.length > 1
    ? timelinePictures[(timelineIdx - 1 + timelinePictures.length) % timelinePictures.length]
    : null;
  const nextTimelinePicture = timelinePictures.length > 1
    ? timelinePictures[(timelineIdx + 1) % timelinePictures.length]
    : null;

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section
        data-testid="hero-section"
        className="relative min-h-[85vh] flex items-center justify-center overflow-hidden"
      >
        {banners.map((b, i) => {
          const src = b.external ? b.image_url : `${BACKEND_URL}${b.image_url}`;
          return (
            <div
              key={`${b.image_url}-${i}`}
              className="absolute inset-0 bg-cover bg-center transition-opacity duration-700 ease-in-out"
              style={{
                backgroundImage: `url(${src})`,
                opacity: heroIdx === i ? 1 : 0,
              }}
            />
          );
        })}
        <div className="absolute inset-0 hero-overlay" />

        {/* Banner chip */}
        {currentChip && (
          <span
            data-testid="hero-caption"
            className="absolute bottom-5 right-5 md:bottom-7 md:right-8 z-20 px-3 py-1.5 rounded-full text-xs font-medium tracking-wide"
            style={{
              backgroundColor: 'rgba(247, 245, 240, 0.92)',
              color: '#1A5C37',
              backdropFilter: 'blur(6px)',
              WebkitBackdropFilter: 'blur(6px)',
            }}
          >
            {currentChip}
          </span>
        )}
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 text-center">
          <p
            className="text-xs uppercase tracking-[0.2em] font-medium mb-6 opacity-0 animate-fade-in-up"
            style={{ color: '#B85C38', animationDelay: '100ms', animationFillMode: 'forwards' }}
          >
            {t("hero_tagline")}
          </p>
          <h1
            className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium text-white mb-6 opacity-0 animate-fade-in-up"
            style={{ animationDelay: '200ms', animationFillMode: 'forwards' }}
          >
            {t("hero_heading")}
          </h1>
          <p
            className="text-base sm:text-lg leading-relaxed max-w-2xl mx-auto mb-10 opacity-0 animate-fade-in-up"
            style={{ color: 'rgba(247, 245, 240, 0.85)', animationDelay: '350ms', animationFillMode: 'forwards' }}
          >
            {t("hero_desc")}
          </p>
          <div
            className="flex flex-col sm:flex-row items-center justify-center gap-4 opacity-0 animate-fade-in-up"
            style={{ animationDelay: '500ms', animationFillMode: 'forwards' }}
          >
            <Link
              to="/about"
              data-testid="hero-learn-more-btn"
              className="px-7 py-3 rounded-full text-sm font-medium transition-colors duration-200"
              style={{ backgroundColor: '#B85C38', color: '#F7F5F0' }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#964B2E'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#B85C38'}
            >
              {t("hero_learn_more")}
            </Link>
            <button
              onClick={openModal}
              data-testid="hero-get-involved-btn"
              className="px-7 py-3 rounded-full text-sm font-medium border transition-colors duration-200"
              style={{ borderColor: 'rgba(247, 245, 240, 0.4)', color: '#F7F5F0' }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'rgba(247, 245, 240, 0.1)';
                e.target.style.borderColor = 'rgba(247, 245, 240, 0.7)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.borderColor = 'rgba(247, 245, 240, 0.4)';
              }}
            >
              {t("nav_get_involved")}
            </button>
          </div>
        </div>
      </section>

      {/* Marquee Ribbon */}
      <section
        data-testid="marquee-section"
        className="py-5 border-y"
        style={{ backgroundColor: '#E8EDE9', borderColor: '#D3DCD6' }}
      >
        <Marquee gradient={false} speed={35} pauseOnHover>
          {marqueeItems.map((item, i) => (
            <span
              key={i}
              className="font-heading text-lg md:text-xl font-medium px-8 md:px-16"
              style={{ color: '#1A5C37' }}
            >
              {item}
              <span className="ml-8 md:ml-16" style={{ color: '#B85C38' }}>&#x2022;</span>
            </span>
          ))}
        </Marquee>
      </section>

      {/* Introductory Content */}
      <section
        data-testid="intro-section"
        className="py-24 md:py-32"
        style={{ backgroundColor: '#F7F5F0' }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 lg:gap-16">
            <div className="md:col-span-4">
              <p
                className="text-xs uppercase tracking-[0.2em] font-medium mb-3"
                style={{ color: '#B85C38' }}
              >
                {t("intro_tagline")}
              </p>
              <h2
                className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium"
                style={{ color: '#1C211F' }}
              >
                {t("intro_heading")}
              </h2>
            </div>
            <div className="md:col-span-8 space-y-6">
              <p
                className="text-base leading-relaxed"
                style={{ color: '#4A5450' }}
              >
                {t("intro_p1")}
              </p>
              <p
                className="text-base leading-relaxed"
                style={{ color: '#4A5450' }}
              >
                {t("intro_p2")}
              </p>
              <Link
                to="/about"
                data-testid="intro-read-more-btn"
                className="inline-block mt-2 text-sm font-medium transition-colors duration-200 pb-0.5 border-b"
                style={{ color: '#1A5C37', borderColor: '#1A5C37' }}
                onMouseEnter={(e) => {
                  e.target.style.color = '#14482B';
                  e.target.style.borderColor = '#14482B';
                }}
                onMouseLeave={(e) => {
                  e.target.style.color = '#1A5C37';
                  e.target.style.borderColor = '#1A5C37';
                }}
              >
                {t("intro_read_more")}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {timelinePictures.length > 0 && (
        <section
          className="py-24 md:py-32"
          style={{ backgroundColor: '#E8EDE9' }}
        >
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="text-center mb-12">
              <p
                className="text-xs uppercase tracking-[0.2em] font-medium mb-3"
                style={{ color: '#B85C38' }}
              >
                Transformation
              </p>
              <h2
                className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium mb-4"
                style={{ color: '#1C211F' }}
              >
                The Timeline
              </h2>
              <p
                className="text-base leading-relaxed max-w-2xl mx-auto"
                style={{ color: '#4A5450' }}
              >
                Bellandur Lake — from a smog-choked, polluted waterbody to a clear, reflective lake with thriving greenery. This is the future KYBIA is working towards.
              </p>
            </div>

            <div className="relative overflow-hidden aspect-[16/9.1]">
              <div key={timelineIdx} className="timeline-swipe-row flex h-full items-stretch justify-center gap-4 md:gap-6">
                {previousTimelinePicture && (
                  <div
                    className="hidden sm:block flex-shrink-0 w-[12%] h-full rounded-xl overflow-hidden border shadow-sm"
                    style={{ borderColor: 'rgba(26,92,55,0.1)', backgroundColor: '#fff' }}
                  >
                    <img
                      src={`${BACKEND_URL}${previousTimelinePicture.image_url}`}
                      alt={previousTimelinePicture.title || "Previous timeline picture"}
                      className="w-full h-full object-cover"
                      loading="eager"
                      decoding="sync"
                    />
                  </div>
                )}

                <div className="relative flex-shrink-0 w-full sm:w-[76%] h-full rounded-xl overflow-hidden border shadow-sm" style={{ borderColor: 'rgba(26,92,55,0.1)', backgroundColor: '#fff' }}>
                  <div className="relative h-full">
                    <img
                      key={currentTimelinePicture.id}
                      src={`${BACKEND_URL}${currentTimelinePicture.image_url}`}
                      alt={currentTimelinePicture.title || "Timeline picture"}
                      className="w-full h-full object-cover"
                      loading="eager"
                      decoding="sync"
                    />
                    {currentTimelinePicture.title && (
                      <div
                        className="absolute left-4 bottom-4 md:left-6 md:bottom-6 px-3 py-1.5 rounded-full text-xs md:text-sm font-medium"
                        style={{ backgroundColor: 'rgba(247,245,240,0.92)', color: '#1A5C37', backdropFilter: 'blur(6px)' }}
                      >
                        {currentTimelinePicture.title}
                      </div>
                    )}
                  </div>
                </div>

                {nextTimelinePicture && (
                  <div
                    className="hidden sm:block flex-shrink-0 w-[12%] h-full rounded-xl overflow-hidden border shadow-sm"
                    style={{ borderColor: 'rgba(26,92,55,0.1)', backgroundColor: '#fff' }}
                  >
                    <img
                      src={`${BACKEND_URL}${nextTimelinePicture.image_url}`}
                      alt={nextTimelinePicture.title || "Next timeline picture"}
                      className="w-full h-full object-cover"
                      loading="eager"
                      decoding="sync"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Focus Areas */}
      <section
        data-testid="focus-areas-section"
        className="py-24 md:py-32"
        style={{ backgroundColor: '#F7F5F0' }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p
              className="text-xs uppercase tracking-[0.2em] font-medium mb-3"
              style={{ color: '#B85C38' }}
            >
              {t("focus_tagline")}
            </p>
            <h2
              className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium"
              style={{ color: '#1C211F' }}
            >
              {t("focus_heading")}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {focusAreas.map((area, i) => {
              const Icon = focusIcons[i] || Leaf;
              return (
                <div
                  key={i}
                  data-testid={`focus-card-${i}`}
                  className="kybia-card p-8 rounded-lg border"
                  style={{
                    backgroundColor: '#F7F5F0',
                    borderColor: 'rgba(26, 92, 55, 0.1)',
                  }}
                >
                  <div
                    className="w-12 h-12 rounded-lg flex items-center justify-center mb-5"
                    style={{ backgroundColor: 'rgba(26, 92, 55, 0.08)' }}
                  >
                    <Icon size={24} weight="duotone" style={{ color: '#1A5C37' }} />
                  </div>
                  <h3
                    className="font-heading text-xl font-medium mb-3"
                    style={{ color: '#1C211F' }}
                  >
                    {area.title}
                  </h3>
                  <p className="text-sm leading-relaxed" style={{ color: '#4A5450' }}>
                    {area.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        data-testid="cta-section"
        className="py-24 md:py-32"
        style={{ backgroundColor: '#E8EDE9' }}
      >
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <h2
            className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium mb-5"
            style={{ color: '#1C211F' }}
          >
            {t("cta_heading")}
          </h2>
          <p
            className="text-base leading-relaxed mb-8 max-w-xl mx-auto"
            style={{ color: '#4A5450' }}
          >
            {t("cta_desc")}
          </p>
          <button
            onClick={openModal}
            data-testid="cta-get-involved-btn"
            className="inline-block px-8 py-3.5 rounded-full text-sm font-medium transition-colors duration-200"
            style={{ backgroundColor: '#B85C38', color: '#F7F5F0' }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#964B2E'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#B85C38'}
          >
            {t("cta_btn")}
          </button>
        </div>
      </section>

      {/* Homepage Popup Modal */}
      {activePopup && (
        <>
          {/* Overlay */}
          <div
            className="fixed inset-0 z-50 transition-opacity duration-300"
            style={{
              backgroundColor: "rgba(0,0,0,0.55)",
              opacity: popupVisible ? 1 : 0,
              pointerEvents: popupVisible ? "auto" : "none",
            }}
            onClick={dismissPopup}
          />

          {/* Modal Card */}
          <div
            className="fixed z-50 transition-all duration-300"
            style={{
              top: "50%",
              left: "50%",
              transform: popupVisible
                ? "translate(-50%, -50%) scale(1)"
                : "translate(-50%, -48%) scale(0.96)",
              opacity: popupVisible ? 1 : 0,
              width: "min(640px, calc(100vw - 2rem))",
              maxHeight: "calc(100dvh - 2rem)",
            }}
            data-testid="homepage-popup"
          >
            <div
              className="rounded-2xl flex flex-col overflow-hidden"
              style={{ backgroundColor: "#fff", boxShadow: "0 24px 64px rgba(0,0,0,0.22)", maxHeight: "calc(100dvh - 2rem)" }}
            >
              {/* Top accent bar */}
              <div className="h-1.5 w-full flex-shrink-0" style={{ backgroundColor: "#1A5C37" }} />

              {/* Header — always visible */}
              <div className="flex items-start gap-3 px-5 pt-4 pb-3 flex-shrink-0">
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                  style={{ backgroundColor: "rgba(26,92,55,0.1)" }}
                >
                  <Leaf size={17} weight="duotone" style={{ color: "#1A5C37" }} />
                </div>
                <h2
                  className="font-heading text-lg font-medium flex-1 leading-snug"
                  style={{ color: "#1C211F" }}
                  data-testid="popup-title"
                >
                  {activePopup.title}
                </h2>
                <button
                  onClick={dismissPopup}
                  className="p-1.5 rounded-full transition-colors flex-shrink-0 border"
                  style={{ color: "#4A5450", borderColor: "rgba(74,84,80,0.35)" }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "rgba(26,92,55,0.08)";
                    e.currentTarget.style.borderColor = "rgba(26,92,55,0.4)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.borderColor = "rgba(74,84,80,0.35)";
                  }}
                  data-testid="popup-close-btn"
                >
                  <X size={17} weight="bold" />
                </button>
              </div>

              {/* Scrollable description */}
              <div className="overflow-y-auto px-5 pb-2" style={{ minHeight: 0 }}>
                <p
                  className="text-sm leading-relaxed"
                  style={{ color: "#4A5450" }}
                  data-testid="popup-description"
                >
                  {activePopup.description}
                </p>
              </div>

              {/* Multi-popup navigator — only shown when there are 2+ popups */}
              {popupList.length > 1 && (
                <div className="flex items-center justify-center gap-3 px-5 pt-3 pb-1 flex-shrink-0">
                  {/* Prev */}
                  <button
                    onClick={() => setPopupIdx((i) => Math.max(0, i - 1))}
                    disabled={popupIdx === 0}
                    className="p-1 rounded-full transition-colors"
                    style={{
                      color: popupIdx === 0 ? "rgba(74,84,80,0.25)" : "#1A5C37",
                      cursor: popupIdx === 0 ? "not-allowed" : "pointer",
                    }}
                    data-testid="popup-prev-btn"
                  >
                    <CaretLeft size={16} weight="bold" />
                  </button>

                  {/* Dot indicators */}
                  <div className="flex items-center gap-1.5">
                    {popupList.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setPopupIdx(i)}
                        className="rounded-full transition-all duration-200"
                        style={{
                          width: i === popupIdx ? 20 : 8,
                          height: 8,
                          backgroundColor: i === popupIdx ? "#1A5C37" : "rgba(26,92,55,0.25)",
                        }}
                        data-testid={`popup-dot-${i}`}
                      />
                    ))}
                  </div>

                  {/* Next */}
                  <button
                    onClick={() => setPopupIdx((i) => Math.min(popupList.length - 1, i + 1))}
                    disabled={popupIdx === popupList.length - 1}
                    className="p-1 rounded-full transition-colors"
                    style={{
                      color: popupIdx === popupList.length - 1 ? "rgba(74,84,80,0.25)" : "#1A5C37",
                      cursor: popupIdx === popupList.length - 1 ? "not-allowed" : "pointer",
                    }}
                    data-testid="popup-next-btn"
                  >
                    <CaretRight size={16} weight="bold" />
                  </button>
                </div>
              )}

              {/* Footer — shown when a report or link is linked */}
              {(activePopup.report_file_url || activePopup.link_url) && (
                <div className="px-5 py-4 flex-shrink-0" style={{ borderTop: "1px solid rgba(26,92,55,0.08)" }}>
                  {activePopup.report_file_url ? (
                    /* Report type */
                    <a
                      href={`${BACKEND_URL}${activePopup.report_file_url}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-colors"
                      style={{ backgroundColor: "#1A5C37", color: "#F7F5F0" }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#14482B")}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#1A5C37")}
                      data-testid="popup-view-report-btn"
                    >
                      <ArrowSquareOut size={14} weight="bold" />
                      {activePopup.button_text || "View Report"}
                    </a>
                  ) : activePopup.link_url && (activePopup.popup_type === "external_link" || /^https?:\/\//.test(activePopup.link_url)) ? (
                    /* External link — opens in new tab */
                    <a
                      href={activePopup.link_url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-colors"
                      style={{ backgroundColor: "#1A5C37", color: "#F7F5F0" }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#14482B")}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#1A5C37")}
                      data-testid="popup-external-link-btn"
                    >
                      <ArrowSquareOut size={14} weight="bold" />
                      {activePopup.button_text || "Learn More"}
                    </a>
                  ) : activePopup.link_url ? (
                    /* Internal link — same tab via React Router */
                    <Link
                      to={activePopup.link_url}
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-colors"
                      style={{ backgroundColor: "#1A5C37", color: "#F7F5F0" }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#14482B")}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#1A5C37")}
                      onClick={dismissPopup}
                      data-testid="popup-internal-link-btn"
                    >
                      <ArrowSquareIn size={14} weight="bold" />
                      {activePopup.button_text || "Learn More"}
                    </Link>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
